<template>
  <settings-modal
    class="savings-acc-transaction-update-modal t-savings-acc-transaction-update-modal"
    hide-container
  >
    <template slot="heading">{{ title }}</template>

    <scrollable-container slot="raw">
      <div>
        <data-row>
          <v-flex>
            <one-data-column>
              <data-label slot="label">Amount and currency</data-label>
              <div class="t-amount">{{ transaction.amount | number }} {{ transaction.currency }}</div>
            </one-data-column>

            <one-data-column
              :label-props="{ style: 'display: none;' }"
              :data-props="{ style: 'max-width: 575px;' }"
            >
              <v-layout row>
                <v-flex xs12>
                  <date-picker
                    v-model="form.settlementDate"
                    class="t-settlement-date-field"
                    label="Settlement date"
                    prepend-icon="event"
                    :max="maxSettlementDate"
                    :disabled="!canEditTransaction"
                    :error-messages="getValidationErrorMessage('settlementDate')"
                  />
                </v-flex>
              </v-layout>
            </one-data-column>

            <one-data-column
              :label-props="{ style: 'display: none;' }"
              :data-props="{ style: 'max-width: 575px;' }"
            >
              <v-layout row>
                <v-flex xs12>
                  <v-text-field
                    v-model.trim="form.paymentId"
                    class="t-payment-id-field"
                    label="Payment ID"
                    :disabled="!canEditTransaction"
                    :error-messages="getValidationErrorMessage('paymentId')"
                  />
                </v-flex>
              </v-layout>
            </one-data-column>

            <one-data-column
              v-if="isWithdrawalTransaction"
              :label-props="{ style: 'display: none;' }"
              :data-props="{ style: 'max-width: 575px;' }"
            >
              <v-layout row>
                <v-flex xs12>
                  <v-text-field
                    v-model.trim="form.interestAmount"
                    class="t-interest-amount-field"
                    label="Interest amount"
                    type="number"
                    :disabled="!canEditTransaction"
                    :error-messages="getValidationErrorMessage('interestAmount')"
                  />
                </v-flex>
              </v-layout>
            </one-data-column>
          </v-flex>
        </data-row>

        <error-alert :error="error" />
      </div>
    </scrollable-container>

    <template slot="actions">
      <v-btn
        class="t-save-button"
        color="primary"
        medium
        flat
        :disabled="!canEditTransaction || !isFormFilled || loading"
        @click="onSaveButtonClick"
      >save</v-btn>

      <v-btn
        class="t-close-button"
        color="primary"
        medium
        flat
        @click="closeModal"
      >cancel</v-btn>

      <v-spacer />

      <v-btn
        v-if="isDeclineButtonVisible"
        class="mr-0 t-decline-button"
        color="red"
        :disabled="!canEditTransaction"
        medium
        flat
        @click="onDeclineButtonClick"
      >decline</v-btn>
    </template>
  </settings-modal>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  // Utils
  import { formatDate } from '@/utils/Date'

  // Constants
  import { ROLES } from '@/store/admin'
  import { CLIENT_REQUEST_TYPES } from '@/models/ClientRequest'

  // Events
  import { EVENT_SAVINGS_ACCOUNT_TRANSACTION_UPDATE } from '@/events/savingsAccount'

  // Services
  import ApiRequest from '@/services/ApiRequests/ApiRequest'
  import SavingsAccountApi from '@/services/Api/SavingsAccountApi'

  // Mixins
  import form from '@/mixins/form'

  // Components
  import SettingsModal from '@/components/modals/SettingsModal'
  import ScrollableContainer from '@/components/ui/ScrollableContainer'
  import DataRow from '@/components/ui/Data/DataRow'
  import OneDataColumn from '@/components/ui/Data/OneDataColumn'
  import DataLabel from '@/components/ui/Data/DataLabel'
  import DatePicker from '@/components/ui/DatePicker'
  import ErrorAlert from '@/components/ui/ErrorAlert'

  const titleMap = {
    [CLIENT_REQUEST_TYPES.DEPOSIT]: 'Saving account opening transaction',
    [CLIENT_REQUEST_TYPES.SAVINGS_REPLENISHMENT]: 'Replenishment transaction',
    [CLIENT_REQUEST_TYPES.SAVINGS_WITHDRAWAL]: 'Withdrawal transaction'
  }

  export default {
    name: 'SavingsAccountTransactionUpdateModal',
    components: {
      SettingsModal,
      ScrollableContainer,
      DataRow,
      OneDataColumn,
      DataLabel,
      DatePicker,
      ErrorAlert
    },
    mixins: [form],
    data () {
      return {
        form: { settlementDate: null, paymentId: null, interestAmount: undefined },
        updateTransactionApiRequest: new ApiRequest(SavingsAccountApi.updateTransaction),
        declineTransactionApiRequest: new ApiRequest(SavingsAccountApi.declineTransaction)
      }
    },
    computed: {
      ...mapGetters('admin', ['hasRole']),
      ...mapGetters('modal', ['modal']),
      title () {
        return `${titleMap[this.transaction.clientRequestType]} ${this.transaction.id}`
      },
      transaction () {
        return this.modal.data.transaction
      },
      maxSettlementDate () {
        return formatDate(new Date())
      },
      loading () {
        return this.updateTransactionApiRequest.loading || this.declineTransactionApiRequest.loading
      },
      error () {
        return this.updateTransactionApiRequest.error || this.declineTransactionApiRequest.error
      },
      requiredFields () {
        const requiredFields = ['settlementDate', 'paymentId']

        if (this.isWithdrawalTransaction) {
          requiredFields.push('interestAmount')
        }

        return requiredFields
      },
      isWithdrawalTransaction () {
        return this.transaction.clientRequestType === CLIENT_REQUEST_TYPES.SAVINGS_WITHDRAWAL
      },
      isDeclineButtonVisible () {
        return this.transaction.settlementDate == null && this.transaction.paymentId == null
      },
      canEditTransaction () {
        return this.hasRole([ROLES.MANAGER])
      }
    },
    created () {
      this.setFormData({
        settlementDate: this.transaction.settlementDate,
        paymentId: this.transaction.paymentId
      })
    },
    methods: {
      ...mapActions('modal', ['showModal', 'closeModal']),
      onCommentTemplateChange (value) {
        this.setCommentField(value)
      },
      onSaveButtonClick () {
        this.updateTransactionApiRequest.execute(
          this.transaction.savingsAccountId,
          this.transaction.id,
          this.form
        )
          .then(() => {
            this.emitUpdateEvent()
            this.closeModal()
            this.showSuccessFlashMessage('Transaction has been successfully updated.')
          })
          .catch(() => {})
      },
      onDeclineButtonClick () {
        this.declineTransactionApiRequest.execute(
          this.transaction.savingsAccountId,
          this.transaction.id
        )
          .then(() => {
            this.emitUpdateEvent()
            this.closeModal()
            this.showSuccessFlashMessage('Transaction has been successfully declined.')
          })
          .catch(() => {})
      },
      emitUpdateEvent () {
        this.$eventHub.$emit(EVENT_SAVINGS_ACCOUNT_TRANSACTION_UPDATE)
      },
      showSuccessFlashMessage (message) {
        setTimeout(() => {
          this.showModal({
            name: 'SuccessFlashModal',
            data: { message }
          })
        }, 100)
      }
    }
  }
</script>
